import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext, CartContext } from '../App';
import { ShoppingCart, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

export default function ProductCard({ product }) {
  const { user } = useContext(AuthContext);
  const { addToCart } = useContext(CartContext);

  const userTier = user?.account_tier || 'retail';
  const price = product.pricing[userTier] || product.pricing.retail;
  const hasTradePrice = product.pricing.trade && userTier === 'trade';

  const handleAddToCart = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }
    try {
      await addToCart(product.id);
      toast.success('Added to cart');
    } catch (err) {
      toast.error('Failed to add to cart');
    }
  };

  const inStock = product.stock_qty > 0;

  return (
    <Link to={`/products/${product.slug}`} data-testid={`product-card-${product.slug}`}>
      <div className="bg-card border border-border rounded-sm overflow-hidden card-hover group h-full flex flex-col">
        {/* Image */}
        <div className="aspect-square relative overflow-hidden bg-zinc-900">
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-600">
              No Image
            </div>
          )}
          {!inStock && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white font-bold uppercase tracking-wide">Out of Stock</span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex-1 flex flex-col">
          <p className="text-xs text-zinc-500 uppercase tracking-wide mb-1">{product.brand}</p>
          <h3 className="text-foreground font-bold text-sm mb-2 line-clamp-2 group-hover:text-[#78BE20] transition-colors">
            {product.name}
          </h3>
          <p className="text-xs text-muted-foreground font-mono mb-3">SKU: {product.sku}</p>

          <div className="mt-auto space-y-2">
            {/* Pricing */}
            <div className="flex items-end justify-between">
              <div>
                <p className="text-[#78BE20] font-bold text-lg font-mono">
                  ${price.toFixed(2)}
                </p>
                {hasTradePrice && (
                  <p className="text-xs text-zinc-500 line-through">${product.pricing.retail.toFixed(2)}</p>
                )}
              </div>
              {!user && (
                <div className="flex items-center text-xs text-zinc-500">
                  <Lock className="h-3 w-3 mr-1" />
                  <span>Login for pricing</span>
                </div>
              )}
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              disabled={!inStock}
              className="w-full bg-[#78BE20] text-black hover:bg-[#65a31b] disabled:opacity-50 disabled:cursor-not-allowed"
              size="sm"
              data-testid={`add-to-cart-${product.slug}`}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {inStock ? 'Add to Cart' : 'Out of Stock'}
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
}
